import { useAgencyCampaignContext } from '../agencyCampaignContext';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import type { SupplyListFilter } from './model';
import {
  SupplyTab,
  parseSupplyRouteSearch,
  serializeSupplyRouteSearch,
  type SupplyRouteSearch,
} from './supplyRouteSearch';

function useRouteFilters(
  search: SupplyRouteSearch,
  navigateSearch: (search: SupplyRouteSearch) => void,
) {
  const filter = useMemo(() => parseSupplyRouteSearch(search), [search]);
  const update = useCallback(
    (next: SupplyListFilter) => {
      navigateSearch(serializeSupplyRouteSearch(next));
    },
    [navigateSearch],
  );
  return {
    filter,
    setSearch: (searchTerm: string) => {
      update({ ...filter, searchTerm, page: 0 });
    },
    setPagination: (page: number, size: number) => {
      update({ ...filter, page, size });
    },
  };
}

export function useSupplyAppsRouteFilters() {
  const scope = useAgencyCampaignContext();
  const navigate = useNavigate({
    from: scope ? '/a/$agencyId/campaigns/$section' : '/campaigns/supply/apps',
  });
  const navigateSearch = useCallback(
    (search: SupplyRouteSearch) =>
      void navigate({
        search: { ...search, ...(scope ? { supplyTab: SupplyTab.Apps } : {}) },
        replace: true,
      }),
    [navigate, scope],
  );
  return useRouteFilters(useSearch({ strict: false }), navigateSearch);
}

export function useSupplyDomainsRouteFilters() {
  const scope = useAgencyCampaignContext();
  const navigate = useNavigate({
    from: scope ? '/a/$agencyId/campaigns/$section' : '/campaigns/supply/domains',
  });
  const navigateSearch = useCallback(
    (search: SupplyRouteSearch) =>
      void navigate({
        search: { ...search, ...(scope ? { supplyTab: SupplyTab.Domains } : {}) },
        replace: true,
      }),
    [navigate, scope],
  );
  return useRouteFilters(useSearch({ strict: false }), navigateSearch);
}

export function useTailoredSupplyRouteFilters() {
  const scope = useAgencyCampaignContext();
  const navigate = useNavigate({
    from: scope ? '/a/$agencyId/campaigns/$section' : '/campaigns/supply/tailored-supply/',
  });
  const navigateSearch = useCallback(
    (search: SupplyRouteSearch) =>
      void navigate({
        search: { ...search, ...(scope ? { supplyTab: SupplyTab.Tailored } : {}) },
        replace: true,
      }),
    [navigate, scope],
  );
  return useRouteFilters(useSearch({ strict: false }), navigateSearch);
}
